import { createBrowserRouter, Navigate /* Outlet */ } from 'react-router-dom';
import { Suspense, lazy, useEffect, useState } from 'react';
import { Spinner } from '@/components/ui/atoms/spinner';
import ProtectedRoute from './ProtectedRoute'; // Assuming ProtectedRoute is in the same directory
import { LegacySettingsGuard } from '@/components/features/legacy/LegacySettingsGuard';
import { parseDomain } from '@/lib/utils/subdomain';
import { useAuth } from '@/lib/hooks/useAuth';
import { supabase } from '@/lib/supabase';

// --- Layouts ---
// We might need different layouts for Platform vs Community
// const PlatformLayout = lazy(() => import('@/components/layout/PlatformLayout'));
const CommunityLayout = lazy(() =>
  import('@/components/layout/CommunityLayout').then((module) => ({
    default: module.CommunityLayout,
  }))
);
const AdminLayout = lazy(() =>
  import('@/components/layout/AdminLayout').then((module) => ({
    default: module.AdminLayout,
  }))
);
const MemberLayout = lazy(() =>
  import('@/components/features/members/MemberLayout').then((module) => ({
    default: module.MemberLayout,
  }))
);
const MinimalLayout = lazy(() => import('@/components/layout/MinimalLayout')); // For login, signup, errors etc.

// --- Page Components (Lazy Loaded) ---

// Auth & Public Pages
const LandingPage = lazy(() => import('@/pages/LandingPage'));
// --- NEW: Use PlatformLoginPage wrapper ---
const PlatformLoginPage = lazy(() => import('@/pages/auth/PlatformLoginPage'));
// --- NEW: Platform Auth Pages ---
const PlatformSignupPage = lazy(
  () => import('@/pages/auth/PlatformSignupPage')
);
const PlatformForgotPasswordPage = lazy(
  () => import('@/pages/auth/PlatformForgotPasswordPage')
);
const PlatformUpdatePasswordPage = lazy(
  () => import('@/pages/auth/PlatformUpdatePasswordPage')
);
// --- Remove old PlatformLogin/Signup direct imports if they exist ---
// const PlatformLogin = lazy(() => import('@/pages/auth/PlatformLogin'));
// const PlatformSignup = lazy(() => import('@/pages/auth/PlatformSignup'));
const CommunityLogin = lazy(() => import('@/pages/auth/CommunityLogin'));
const CommunitySignup = lazy(() => import('@/pages/auth/CommunitySignup'));
const AuthCallback = lazy(() => import('@/pages/auth/AuthCallback'));
const AuthPendingLogin = lazy(() => import('@/pages/auth/AuthPendingLogin'));
const SessionTransfer = lazy(() => import('@/pages/auth/SessionTransfer'));
const Logout = lazy(() => import('@/pages/auth/Logout'));
// LinkedIn Login Experiment
const LinkedInLoginExperiment = lazy(
  () => import('@/pages/auth/LinkedInLoginExperiment')
);
const LinkedInCallback = lazy(() => import('@/pages/auth/LinkedInCallback'));
const LinkedInOAuthStart = lazy(
  () => import('@/pages/auth/LinkedInOAuthStart')
);
const LinkedInDebug = lazy(() => import('@/pages/auth/LinkedInDebug'));
// LinkedIn Unified OAuth Experiment (single-step flow)
const LinkedInUnifiedLogin = lazy(
  () => import('@/pages/auth/LinkedInUnifiedLogin')
);
const LinkedInUnifiedCallback = lazy(
  () => import('@/pages/auth/LinkedInUnifiedCallback')
);

// Admin Pages
const AdminDashboard = lazy(() =>
  import('@/pages/admin/AdminDashboard').then((module) => ({
    default: module.AdminDashboard,
  }))
);
const AdminJobs = lazy(() =>
  import('@/pages/admin/AdminJobs').then((module) => ({
    default: module.AdminJobs,
  }))
);
const AdminEmployers = lazy(() =>
  import('@/pages/admin/AdminEmployers').then((module) => ({
    default: module.AdminEmployers,
  }))
);
const AdminCommunities = lazy(() =>
  import('@/pages/admin/AdminCommunities').then((module) => ({
    default: module.AdminCommunities,
  }))
);
const AdminUsers = lazy(() =>
  import('@/pages/admin/AdminUsers').then((module) => ({
    default: module.AdminUsers,
  }))
);
const AdminSettings = lazy(() =>
  import('@/pages/admin/AdminSettings').then((module) => ({
    default: module.AdminSettings,
  }))
);
const RSSSourceManagement = lazy(() =>
  import('@/pages/admin/RSSSourceManagement').then((module) => ({
    default: module.RSSSourceManagement,
  }))
);

// Community Owner Pages
const CommunityDashboard = lazy(() =>
  import('@/pages/community/DashboardPage').then((module) => ({
    default: module.DashboardPage,
  }))
);
const Members = lazy(() =>
  import('@/pages/community/Members').then((module) => ({
    default: module.Members,
  }))
);
const JobsManagement = lazy(() =>
  import('@/pages/community/Jobs').then((module) => ({ default: module.Jobs }))
);
const EmployersManagement = lazy(() =>
  import('@/pages/community/Employers').then((module) => ({
    default: module.Employers,
  }))
);
const EventsManagement = lazy(() =>
  import('@/pages/community/EventsManagement').then((module) => ({
    default: module.EventsManagement,
  }))
);
const ApplicationSettings = lazy(
  () => import('@/pages/community/ApplicationSettings')
);
const SocialApplyAnalytics = lazy(() =>
  import('@/pages/community/SocialApplyAnalytics').then((module) => ({
    default: module.default,
  }))
);
const SettingsPlaceholder = lazy(
  () => import('@/pages/community/SettingsPlaceholder')
);
const TodayPage = lazy(() =>
  import('@/pages/community/TodayPage').then((module) => ({
    default: module.TodayPage,
  }))
);
// const CustomizationPortal = lazy(() =>
//   import('@/pages/community/CustomizationPortal').then((module) => ({
//     default: module.CustomizationPortal,
//   }))
// );
// const AtsIntegrationSettingsPage = lazy(() =>
//   import(
//     '@/components/features/community/settings/integrations/AtsIntegrationSettingsPage'
//   ).then((module) => ({
//     default: module.AtsIntegrationSettingsPage,
//   }))
// );
// Add other community owner pages...

// Community Member / Public Pages
const MemberHub = lazy(() =>
  import('@/pages/member/MemberHub').then((module) => ({
    default: module.MemberHub,
  }))
);
const CommunityApply = lazy(() =>
  import('@/pages/member/CommunityApplyV2').then((module) => ({
    default: module.CommunityApply,
  }))
);
const ApplyToJoinFlow = lazy(
  () => import('@/components/features/apply-to-join/ApplyToJoinFlow')
);
const FollowupForm = lazy(
  () => import('@/components/features/apply-to-join/FollowupForm')
);
const Events = lazy(() =>
  import('@/pages/member/Events').then((module) => ({ default: module.Events }))
);
const EventDetails = lazy(() =>
  import('@/pages/member/EventDetails').then((module) => ({
    default: module.EventDetails,
  }))
);
const Channels = lazy(() =>
  import('@/pages/member/Channels').then((module) => ({
    default: module.Channels,
  }))
);
const Feed = lazy(() =>
  import('@/pages/member/Feed').then((module) => ({ default: module.Feed }))
);
const FeedExperiment = lazy(() =>
  import('@/pages/member/FeedExperiment').then((module) => ({
    default: module.FeedExperimentPage,
  }))
);
const CommunityJobBoardPage = lazy(() =>
  import('@/pages/member/CommunityJobBoardPage').then((module) => ({
    default: module.CommunityJobBoardPage,
  }))
);
const JobDetails = lazy(() =>
  import('@/pages/member/JobDetailsDynamicPage').then((module) => ({
    default: module.JobDetailsDynamicPage,
  }))
);
const JobDetailsDynamicPage = lazy(() =>
  import('@/pages/member/JobDetailsDynamicPage').then((module) => ({
    default: module.JobDetailsDynamicPage,
  }))
);
const SocialApplyLanding = lazy(() =>
  import('@/pages/member/SocialApplyLanding').then((module) => ({
    default: module.default,
  }))
);
const MemberProfile = lazy(() =>
  import('@/pages/member/ModernMemberProfile').then((module) => ({
    default: module.ModernMemberProfile,
  }))
);
const MemberAccountSettings = lazy(() =>
  import('@/pages/member/MemberAccountSettings').then((module) => ({
    default: module.MemberAccountSettings,
  }))
);
const Notifications = lazy(() =>
  import('@/pages/member/Notifications').then((module) => ({
    default: module.Notifications,
  }))
);
const ReaderView = lazy(() =>
  import('@/pages/member/ReaderView').then((module) => ({
    default: module.ReaderView,
  }))
);
const LinkedInExperiment = lazy(() =>
  import('@/pages/member/LinkedInExperiment').then((module) => ({
    default: module.default,
  }))
);
// Add other member pages...

// Error Pages
const NotFound = lazy(() => import('@/pages/NotFound'));
const Unauthorized = lazy(() =>
  import('@/pages/errors/Unauthorized').then((module) => ({
    default: module.Unauthorized,
  }))
);
const CommunityNotFound = lazy(() =>
  import('@/pages/errors/CommunityNotFound').then((module) => ({
    default: module.CommunityNotFound,
  }))
);

// Deep Link Redirect (for /c/{slug}/... paths)
const DeepLinkRedirect = lazy(() =>
  import('@/pages/DeepLinkRedirect').then((module) => ({
    default: module.DeepLinkRedirect,
  }))
);

// Placeholder page for Community/Talent section in NavRail
// const CommunityPlaceholderPage = lazy(() =>
//   import('@/pages/community/CommunityPlaceholderPage').then((module) => ({
//     default: module.CommunityPlaceholderPage,
//   }))
// );

const MembersSettings = lazy(() =>
  import('@/components/features/settings/tabs/MembersSettings').then(
    (module) => ({
      default: module.MembersSettings,
    })
  )
);

const MemberApplicationsQueue = lazy(() =>
  import('@/components/features/settings/tabs/MemberApplicationsQueue').then(
    (module) => ({
      default: module.MemberApplicationsQueue,
    })
  )
);

// TEMP: Use LandingPage as placeholder for onboarding
// const OwnerOnboarding = lazy(() => import('@/pages/onboarding/OwnerOnboarding'));

// Correct the import path based on the re-export file
const OwnerOnboarding = lazy(() =>
  import('@/components/features/onboarding/OwnerOnboarding').then((module) => ({
    default: module.OwnerOnboarding,
  }))
);

const EnhancedMobileOnboarding = lazy(() =>
  import('@/components/features/onboarding/member/EnhancedMobileOnboarding').then(
    (module) => ({
      default: module.EnhancedMobileOnboarding,
    })
  )
);

const GettingToKnowFlow = lazy(
  () => import('@/components/features/onboarding/getting-to-know')
);

// AI Community Setup
const AICommunitySetupWizard = lazy(() =>
  import('@/components/features/community-setup/AICommunitySetupWizard').then(
    (module) => ({
      default: module.AICommunitySetupWizard,
    })
  )
);

// Demo Pages
const ApplicationReferencesDemo = lazy(
  () => import('@/pages/ApplicationReferencesDemo')
);
const DemoIndex = lazy(() => import('@/pages/DemoIndex'));

// Import the new SettingsPageLayout and SettingsSectionRenderer
const SettingsPageLayout = lazy(
  () => import('@/components/features/settings/SettingsPageLayout')
);
const SettingsSectionRenderer = lazy(
  () => import('@/components/features/settings/SettingsSectionRenderer')
);

// --- People Section (New Relationship-Centric Member Management) ---
const PeopleHome = lazy(() =>
  import('@/components/features/people/PeopleHome').then((module) => ({
    default: module.PeopleHome,
  }))
);
const EveryoneDirectory = lazy(() =>
  import('@/components/features/people/EveryoneDirectory').then((module) => ({
    default: module.EveryoneDirectory,
  }))
);
const SegmentsView = lazy(() =>
  import('@/components/features/people/SegmentsView').then((module) => ({
    default: module.SegmentsView,
  }))
);
const PeopleMemberProfile = lazy(() =>
  import('@/components/features/people/MemberProfile').then((module) => ({
    default: module.MemberProfile,
  }))
);
const ApplicationsView = lazy(() =>
  import('@/components/features/people/applications').then((module) => ({
    default: module.ApplicationsView,
  }))
);
const ApplicationDetailView = lazy(() =>
  import('@/components/features/people/applications').then((module) => ({
    default: module.ApplicationDetailView,
  }))
);

// --- Profile Views (New Human-Centered Profile System) ---
const ProfileView = lazy(() =>
  import('@/components/features/people/ProfileView').then((module) => ({
    default: module.ProfileView,
  }))
);

// --- Settings V2 (Simplified Settings Experience) ---
const SettingsHomeV2 = lazy(() =>
  import('@/components/features/settings-v2/SettingsHome').then((module) => ({
    default: module.SettingsHome,
  }))
);
const YourCommunitySettings = lazy(() =>
  import('@/components/features/settings-v2/YourCommunitySettings').then(
    (module) => ({
      default: module.YourCommunitySettings,
    })
  )
);
const MemberExperienceSettings = lazy(() =>
  import('@/components/features/settings-v2/MemberExperienceSettings').then(
    (module) => ({
      default: module.MemberExperienceSettings,
    })
  )
);
const IntegrationsSettingsV2 = lazy(() =>
  import('@/components/features/settings-v2/IntegrationsSettings').then(
    (module) => ({
      default: module.IntegrationsSettings,
    })
  )
);
const TeamSettings = lazy(() =>
  import('@/components/features/settings-v2/TeamSettings').then((module) => ({
    default: module.TeamSettings,
  }))
);
const BillingSettings = lazy(() =>
  import('@/components/features/settings-v2/BillingSettings').then(
    (module) => ({
      default: module.BillingSettings,
    })
  )
);

// --- Helper for Suspense Fallback ---
const SuspenseFallback = () => (
  <div className="min-h-screen flex items-center justify-center bg-brand-background">
    <Spinner size="lg" />
  </div>
);

// Context-aware login component
function ContextAwareLogin() {
  const domainInfo = parseDomain();

  if (domainInfo.type === 'community') {
    return <CommunityLogin />;
  } else {
    return <PlatformLoginPage />;
  }
}

// Context-aware signup component
function ContextAwareSignup() {
  const domainInfo = parseDomain();

  if (domainInfo.type === 'community') {
    return <CommunitySignup />;
  } else {
    return <PlatformSignupPage />;
  }
}

// Context-aware onboarding component
function ContextAwareOnboarding() {
  const { user, loading } = useAuth();
  const domainInfo = parseDomain();
  const [checkingOnboarding, setCheckingOnboarding] = useState(true);
  const [shouldShowOnboarding, setShouldShowOnboarding] = useState(true);

  useEffect(() => {
    const checkOwnerOnboarding = async () => {
      if (!loading && user?.profile?.role === 'owner') {
        try {
          const { data: ownedCommunity } = await supabase
            .from('communities')
            .select('slug, settings')
            .eq('owner_id', user.id)
            .single();

          const settings = ownedCommunity?.settings as any;
          if (ownedCommunity && settings?.onboarding_completed) {
            // Onboarding is complete, redirect to settings dashboard
            const communitySlug = ownedCommunity.slug;
            const isDevelopment =
              window.location.hostname.includes('localhost');
            const baseHost = isDevelopment
              ? 'localhost:5173'
              : window.location.hostname.replace(/^[^.]+\./, '');

            // Redirect to community settings
            window.location.href = `${window.location.protocol}//${communitySlug}.${baseHost}/settings`;
            setShouldShowOnboarding(false);
          }
        } catch (error) {
          console.error('Error checking owner onboarding:', error);
        }
      }
      setCheckingOnboarding(false);
    };

    if (!loading) {
      checkOwnerOnboarding();
    }
  }, [user, loading]);

  // Show loading spinner while checking auth state or onboarding status
  if (loading || checkingOnboarding) {
    return <SuspenseFallback />;
  }

  // If user is an owner and should show onboarding
  if (user?.profile?.role === 'owner' && shouldShowOnboarding) {
    return <OwnerOnboarding />;
  }

  // If in community context and user is a member, show member onboarding
  if (domainInfo.type === 'community' && user?.profile?.role === 'member') {
    return <EnhancedMobileOnboarding />;
  }

  // Default fallback - redirect based on context
  if (domainInfo.type === 'community') {
    return <Navigate to="/member-onboarding" replace />;
  } else {
    return <Navigate to="/onboarding" replace />;
  }
}

// --- Route Definitions ---
export const router = createBrowserRouter([
  {
    // Universal routes that work in any context
    element: (
      <Suspense fallback={<SuspenseFallback />}>
        <ProtectedRoute requiredContext="any" />
      </Suspense>
    ),
    children: [
      {
        path: '/login',
        element: <ContextAwareLogin />,
      },
      {
        path: '/signup',
        element: <ContextAwareSignup />,
      },
    ],
  },
  {
    // App Context Routes (Platform-specific routes)
    element: (
      <Suspense fallback={<SuspenseFallback />}>
        <ProtectedRoute requiredContext="app" />
      </Suspense>
    ),
    children: [
      { path: '/forgot-password', element: <PlatformForgotPasswordPage /> },
    ],
  },
  // Smart onboarding route (detects role and context automatically)
  {
    element: (
      <Suspense fallback={<SuspenseFallback />}>
        <ProtectedRoute requiredContext="any" />
      </Suspense>
    ),
    children: [
      {
        path: '/onboarding',
        element: <ContextAwareOnboarding />,
      },
    ],
  },
  // Owner onboarding route (specific for owners)
  {
    element: (
      <Suspense fallback={<SuspenseFallback />}>
        <ProtectedRoute requiredContext="any" requiredRole="owner" />
      </Suspense>
    ),
    children: [
      {
        path: '/owner-onboarding',
        element: <OwnerOnboarding />,
      },
    ],
  },
  // Member onboarding route (community context only, requires authentication)
  {
    element: (
      <Suspense fallback={<SuspenseFallback />}>
        <ProtectedRoute requiredContext="community" requiredRole="member" />
      </Suspense>
    ),
    children: [
      {
        path: '/member-onboarding',
        element: <GettingToKnowFlow />,
      },
    ],
  },
  {
    // Authenticated Platform Context Routes - no settings route here
    element: (
      <Suspense fallback={<SuspenseFallback />}>
        <ProtectedRoute requiredContext="platform" />
      </Suspense>
    ),
    children: [
      // Platform-only routes go here (but not settings)
      // { path: '/platform-settings', element: <PlatformSettings /> }, // Ensure this is not needed
    ],
  },
  {
    element: (
      <Suspense fallback={<SuspenseFallback />}>
        <ProtectedRoute requiredContext="platform" />
      </Suspense>
    ),
    children: [],
  },

  // --- Admin Routes ---
  {
    path: '/admin',
    element: (
      <Suspense fallback={<SuspenseFallback />}>
        <ProtectedRoute requiredContext="any" requiredRole="admin">
          <AdminLayout />
        </ProtectedRoute>
      </Suspense>
    ),
    children: [
      { index: true, element: <AdminDashboard /> },
      { path: 'dashboard', element: <AdminDashboard /> },
      { path: 'communities', element: <AdminCommunities /> },
      { path: 'jobs', element: <AdminJobs /> },
      { path: 'employers', element: <AdminEmployers /> },
      { path: 'users', element: <AdminUsers /> },
      { path: 'settings', element: <AdminSettings /> },
    ],
  },

  // --- Legacy Settings Routes (Moved from /settings) ---
  // Only accessible in development. Blocked in production via feature flag.
  {
    path: '/legacy-settings',
    element: (
      <Suspense fallback={<SuspenseFallback />}>
        <LegacySettingsGuard>
          <ProtectedRoute requiredContext="community" requiredRole="owner">
            <CommunityLayout />
          </ProtectedRoute>
        </LegacySettingsGuard>
      </Suspense>
    ),
    children: [
      { index: true, element: <CommunityDashboard /> },
      { path: 'dashboard', element: <CommunityDashboard /> },
      { path: 'today', element: <TodayPage /> },
      { path: 'jobs', element: <JobsManagement /> },
      { path: 'employers', element: <EmployersManagement /> },
      { path: 'events', element: <EventsManagement /> },
      { path: 'applications', element: <ApplicationSettings /> },
      { path: 'talent', element: <MembersSettings /> },
      { path: 'analytics/social-apply', element: <SocialApplyAnalytics /> },
      {
        path: 'global',
        element: <SettingsPageLayout />,
        children: [
          { index: true, element: <SettingsSectionRenderer /> },
          { path: '*', element: <SettingsSectionRenderer /> },
        ],
      },
      { path: 'ai-setup', element: <AICommunitySetupWizard /> },
    ],
  },

  // --- New Settings Routes (MVP) ---
  // The new simplified settings experience with Today as the centerpiece
  {
    path: '/settings',
    element: (
      <Suspense fallback={<SuspenseFallback />}>
        <ProtectedRoute requiredContext="community" requiredRole="owner">
          <CommunityLayout />
        </ProtectedRoute>
      </Suspense>
    ),
    children: [
      { index: true, element: <TodayPage /> },
      { path: 'today', element: <TodayPage /> },

      // --- People Section (Relationship-Centric Member Management) ---
      { path: 'people', element: <PeopleHome /> },
      { path: 'people/everyone', element: <EveryoneDirectory /> },
      { path: 'people/segments', element: <SegmentsView /> },
      { path: 'people/member/:id', element: <PeopleMemberProfile /> },
      { path: 'people/applications', element: <ApplicationsView /> },
      { path: 'people/applications/:id', element: <ApplicationDetailView /> },

      // --- Settings V2 (Simplified Settings Experience) ---
      { path: 'settings-v2', element: <SettingsHomeV2 /> },
      { path: 'settings-v2/community', element: <YourCommunitySettings /> },
      { path: 'settings-v2/members', element: <MemberExperienceSettings /> },
      { path: 'settings-v2/integrations', element: <IntegrationsSettingsV2 /> },
      { path: 'settings-v2/team', element: <TeamSettings /> },
      { path: 'settings-v2/billing', element: <BillingSettings /> },
    ],
  },

  // --- Community Owner Routes (Non-Settings) --- (Uses CommunityLayout)
  // These are routes like /community/:slug/dashboard, /community/:slug/jobs etc.
  // For simplicity, we can have a general community layout wrapper here.
  // If community routes are always prefixed with /community/:slug, adjust accordingly.
  // Assuming general community owner views are NOT under /settings anymore.
  {
    // This path could be more specific, e.g., '/community/:slug' if that's the pattern
    // For now, let's make a generic wrapper for community content pages that are NOT settings.
    // This requires careful thought on how direct community access (e.g. community.site.com/jobs) is handled.
    // The CommunityLayout itself derives slug from hostname or query params.
    path: '/community-dashboard', // Example placeholder path for dashboard outside /settings
    // This might need to be the root or /community/:slug
    element: (
      <Suspense fallback={<SuspenseFallback />}>
        <ProtectedRoute requiredContext="community" requiredRole="owner">
          <CommunityLayout />
        </ProtectedRoute>
      </Suspense>
    ),
    children: [
      // Dashboard is now the default for /community-dashboard (or slug root)
      { index: true, element: <CommunityDashboard /> },
      // TODO: Define actual paths for jobs, employers, talent when they are full pages
      // For example:
      // { path: 'jobs', element: <JobsManagement /> },
      // { path: 'employers', element: <EmployersManagement /> },
      // { path: 'talent', element: <CommunityPlaceholderPage /> },
    ],
  },
  // Fallback for old /settings/jobs, /settings/employers if needed, or remove them.
  // If NavRail links directly to /jobs, /employers, those routes need to be top-level or under a community slug prefix.
  // For now, NavRail points to /community/:slug/jobs etc. which isn't a defined route structure yet.
  // Let's assume NavRail links will be updated or these routes will be defined under a /community/:slug structure.

  {
    // Shared Routes (Minimal Layout) - Add new Platform auth routes here
    element: (
      <Suspense fallback={<SuspenseFallback />}>
        <MinimalLayout />
      </Suspense>
    ),
    children: [
      { path: '/auth/callback', element: <AuthCallback /> },
      { path: '/auth/pending-login', element: <AuthPendingLogin /> },
      { path: '/auth/session-transfer', element: <SessionTransfer /> },
      { path: '/auth/linkedin-callback', element: <LinkedInCallback /> },
      { path: '/auth/linkedin-start', element: <LinkedInOAuthStart /> },
      { path: '/auth/linkedin-debug', element: <LinkedInDebug /> },
      {
        path: '/auth/linkedin-unified-callback',
        element: <LinkedInUnifiedCallback />,
      },
      { path: '/linkedin-login', element: <LinkedInLoginExperiment /> },
      { path: '/linkedin-unified-login', element: <LinkedInUnifiedLogin /> },
      {
        path: '/apply-to-join',
        element: (
          <ProtectedRoute requiredContext="community">
            <ApplyToJoinFlow />
          </ProtectedRoute>
        ),
      },
      {
        path: '/apply-to-join/followup/:applicationId',
        element: <FollowupForm />,
      },
      { path: '/logout', element: <Logout /> },
      { path: '/unauthorized', element: <Unauthorized /> },
      { path: '/community-not-found', element: <CommunityNotFound /> },
      { path: '/update-password', element: <PlatformUpdatePasswordPage /> },
    ],
  },
  // --- Member-Facing Public Community Routes --- (Uses MemberLayout)
  // Routes that can be accessed by anyone (access control handled by HOCs)
  {
    element: (
      <Suspense fallback={<SuspenseFallback />}>
        <MemberLayout />
      </Suspense>
    ),
    children: [
      { path: '/jobs', element: <CommunityJobBoardPage /> },
      { path: '/jobs/demo/:mockJobId', element: <JobDetails /> },
      { path: '/jobs/:jobSlugAndId', element: <JobDetailsDynamicPage /> },
      { path: '/apply/:jobId', element: <SocialApplyLanding /> },
      { path: '/apply', element: <CommunityApply /> },
      { path: '/reader', element: <ReaderView /> },
      { path: '/linkedin-test', element: <LinkedInExperiment /> },
      { path: '/demos', element: <DemoIndex /> },
      {
        path: '/demo/application-references',
        element: <ApplicationReferencesDemo />,
      },
    ],
  },
  // Member-only routes that require authentication
  {
    element: (
      <Suspense fallback={<SuspenseFallback />}>
        <ProtectedRoute requiredContext="community">
          <MemberLayout />
        </ProtectedRoute>
      </Suspense>
    ),
    children: [
      { path: '/', element: <Feed /> },
      { path: '/feed', element: <Navigate to="/" replace /> },
      { path: '/legacy-home', element: <MemberHub /> },
      { path: '/events', element: <Events /> },
      { path: '/events/:eventId', element: <EventDetails /> },
      { path: '/channels', element: <Channels /> },
      { path: '/channels/:channelId', element: <Channels /> }, // channelId can be slug or UUID
      { path: '/notifications', element: <Notifications /> },
      { path: '/profile', element: <ProfileView /> },
      { path: '/members/:profileId', element: <ProfileView /> },
      { path: '/settings', element: <MemberAccountSettings /> },
      { path: '/members', element: <Members /> },
      { path: '/linkedin-experiment', element: <LinkedInExperiment /> },
      { path: '/feed-experiment', element: <FeedExperiment /> },
      // Mobile navigation routes
      { path: '/member/communities/:communityId', element: <Feed /> },
      {
        path: '/member/communities/:communityId/channels',
        element: <Channels />,
      },
      {
        path: '/member/communities/:communityId/channels/:channelId',
        element: <Channels />,
      },
      { path: '/member/communities/:communityId/events', element: <Events /> },
      {
        path: '/member/communities/:communityId/jobs',
        element: <CommunityJobBoardPage />,
      },
      {
        path: '/member/communities/:communityId/account',
        element: <ProfileView />,
      },
      {
        path: '/member/communities/:communityId/settings',
        element: <MemberAccountSettings />,
      },
      {
        path: '/member/communities/:communityId/profile',
        element: <ProfileView />,
      },
      {
        path: '/member/communities/:communityId/members/:profileId',
        element: <ProfileView />,
      },
      {
        path: '/member/communities/:communityId/linkedin-experiment',
        element: <LinkedInExperiment />,
      },
    ],
  },
  // Platform root route (no MemberLayout)
  {
    element: (
      <Suspense fallback={<SuspenseFallback />}>
        <ProtectedRoute requiredContext="platform" />
      </Suspense>
    ),
    children: [{ path: '/', element: <LandingPage /> }],
  },
  // App context root route (main domain without subdomain)
  {
    element: (
      <Suspense fallback={<SuspenseFallback />}>
        <ProtectedRoute requiredContext="app" />
      </Suspense>
    ),
    children: [{ path: '/', element: <LandingPage /> }],
  },
  // --- Deep Link Routes (for /c/{slug}/... paths from mobile app) ---
  // These redirect to the community subdomain to maintain the existing routing system.
  // Example: /c/acme/feed -> acme.terrarium.app/feed
  {
    path: '/c/:slug/*',
    element: (
      <Suspense fallback={<SuspenseFallback />}>
        <DeepLinkRedirect />
      </Suspense>
    ),
  },
  {
    path: '/c/:slug',
    element: (
      <Suspense fallback={<SuspenseFallback />}>
        <DeepLinkRedirect />
      </Suspense>
    ),
  },
  {
    // Catch-all Not Found (Minimal Layout)
    path: '*',
    element: (
      <Suspense fallback={<SuspenseFallback />}>
        <MinimalLayout>
          <NotFound />
        </MinimalLayout>
      </Suspense>
    ),
  },
]);
